import { useEffect, useRef, useState } from "react";

import { type WizardTrigger } from "apis/procore";

import { type GuidanceSnapshot } from "../guidanceModel";
import { ignoreGuidanceDialog } from "../guidanceStore";
import { trackWizardDismissed, trackWizardShown } from "../telemetry";
import { usePreserveBodyLayoutWhileModalOpen } from "./guidanceModalLock";
import { IntroPrompt } from "./IntroPrompt";
import { StepsChecklist } from "./StepsChecklist";

export type GuidanceDialogView = "intro" | "steps";

type GuidanceDialogProps = {
    initialView: GuidanceDialogView;
    /** How the steps checklist was opened — stored by guidanceStore and passed through here. */
    trigger: WizardTrigger;
    snapshot: GuidanceSnapshot;
    onForceStepsDone: () => void;
    initialExpandedKey?: GuidanceSnapshot["items"][number]["key"];
};

export function GuidanceDialog({
    initialView,
    trigger,
    snapshot,
    onForceStepsDone,
    initialExpandedKey,
}: GuidanceDialogProps) {
    const [view, setView] = useState<GuidanceDialogView>(initialView);
    const [pending, setPending] = useState(false);
    const pendingRef = useRef(false);

    // Fire gpu_wizard_shown once per dialog instance when the steps view is reached.
    // Using a ref (not state) so it stays stable across re-renders without re-firing the effect.
    const shownFiredRef = useRef(false);
    useEffect(() => {
        if (view === "steps" && !shownFiredRef.current) {
            shownFiredRef.current = true;
            trackWizardShown(trigger, snapshot.situation);
        }
    }, [view, trigger, snapshot.situation]);

    useBlockViewerKeyboard();
    usePreserveBodyLayoutWhileModalOpen();

    const runTerminalIntent = async (action: () => Promise<void>) => {
        if (pendingRef.current) {
            return;
        }
        pendingRef.current = true;
        setPending(true);
        try {
            await action();
        } catch (error) {
            console.error("[GPU guidance] Failed to persist dialog intent.", error);
        } finally {
            onForceStepsDone();
            pendingRef.current = false;
            setPending(false);
        }
    };

    const onConfirm = () => {
        void runTerminalIntent(async () => {
            await ignoreGuidanceDialog();
            trackWizardDismissed(snapshot.situation);
        });
    };

    if (view === "intro") {
        return <IntroPrompt onGetStarted={() => setView("steps")} isWindows={snapshot.isWindows} />;
    }
    return (
        <StepsChecklist
            snapshot={snapshot}
            busy={pending}
            initialExpandedKey={initialExpandedKey}
            onConfirm={onConfirm}
        />
    );
}

const DIALOG_KEYS = new Set(["Tab", "Enter", " ", "Escape"]);

/** Native video controls only — WASD / H must still be blocked so they cannot move the model. */
const VIDEO_MEDIA_KEYS = new Set([
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "m",
    "M",
    "f",
    "F",
    "k",
    "K",
    "MediaPlayPause",
    "MediaStop",
    "MediaTrackNext",
    "MediaTrackPrevious",
]);

function useBlockViewerKeyboard() {
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (DIALOG_KEYS.has(event.key)) {
                return;
            }
            const target = event.target;
            if (target instanceof HTMLElement && target.closest("video") && VIDEO_MEDIA_KEYS.has(event.key)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
        };

        document.addEventListener("keydown", onKeyDown, true);
        return () => document.removeEventListener("keydown", onKeyDown, true);
    }, []);
}
